import { TrendingDown, TrendingUp, Zap } from 'lucide-react';
import { tokenPrice } from '@/data/blockchainData';
import { ResponsiveContainer, Area, AreaChart, Tooltip, XAxis } from 'recharts';
import { TronSocketData } from '@/hooks/useTronSocket';
import { useTranslation } from 'react-i18next';
import { useTrxPrice } from '@/hooks/useTrxPrice';
import { useTrxPriceHistory } from '@/hooks/useTrxPriceHistory';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

interface TokenPriceCardProps {
  socketData?: TronSocketData;
}

const TokenPriceCard = ({ socketData }: TokenPriceCardProps) => {
  const { t } = useTranslation();
  const { data: cmcData } = useTrxPrice();
  const { data: priceHistory } = useTrxPriceHistory(14);

  // Use CoinMarketCap data as primary, fallback to socket/mock data
  const price = cmcData?.price ?? socketData?.trxPrice ?? tokenPrice.price;
  const change24h = cmcData?.change24h ?? socketData?.trxPriceChange24h ?? tokenPrice.change24h;
  const marketCap = cmcData?.marketCap ?? socketData?.trxMarketCap ?? tokenPrice.marketCap;
  const volume24h = cmcData?.volume24h ?? socketData?.trxVolume24h ?? tokenPrice.volume24h;
  const totalSupply = cmcData?.totalSupply ?? socketData?.totalSupply ?? tokenPrice.totalSupply;
  const totalStaked = socketData?.totalStaked ?? tokenPrice.totalStaked;
  const stakedChange = socketData?.stakedChange ?? tokenPrice.stakedChange;

  const isPositive = change24h >= 0;
  
  // Use Binance price history or fallback to mock data
  const chartData = priceHistory ?? [
    { date: '', value: 0.275 },
    { date: '', value: 0.278 },
    { date: '', value: 0.282 },
    { date: '', value: 0.279 },
    { date: '', value: 0.281 },
    { date: '', value: 0.283 },
    { date: '', value: 0.280 },
    { date: '', value: price },
  ];

  return (
    <div className="glass-card p-4 sm:p-6 h-full overflow-hidden">
      <div className="flex items-center gap-2 mb-3 sm:mb-4">
        <Zap className="w-4 sm:w-5 h-4 sm:h-5 text-primary" />
        <span className="font-semibold text-foreground text-sm sm:text-base">{t('tokens.price')} TRX</span>
      </div>
      
      <div className="flex items-start justify-between mb-3 sm:mb-4">
        <div>
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-2xl sm:text-3xl font-bold text-foreground">
              ${typeof price === 'number' ? price.toFixed(4) : price}
            </span>
            <span className={`flex items-center gap-1 text-xs sm:text-sm font-medium px-1.5 sm:px-2 py-0.5 rounded-md sm:rounded-lg ${
              isPositive ? 'text-success bg-success/10' : 'text-destructive bg-destructive/10'
            }`}>
              {isPositive ? <TrendingUp className="w-3 sm:w-3.5 h-3 sm:h-3.5" /> : <TrendingDown className="w-3 sm:w-3.5 h-3 sm:h-3.5" />}
              {typeof change24h === 'number' ? Math.abs(change24h).toFixed(2) : change24h}%
            </span>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 mt-1.5 sm:mt-2 text-xs sm:text-sm">
            <Popover>
              <PopoverTrigger asChild>
                <span className="text-muted-foreground cursor-pointer hover:text-foreground transition-colors">
                  MCap <span className="text-foreground font-medium">
                    ${typeof marketCap === 'number' ? marketCap.toFixed(2) : marketCap}B
                  </span>
                </span>
              </PopoverTrigger>
              <PopoverContent className="w-64 text-sm bg-card border border-border shadow-lg z-50">
                <p>{t('tokens.marketCapTooltip')}</p>
              </PopoverContent>
            </Popover>
            <Popover>
              <PopoverTrigger asChild>
                <span className="text-muted-foreground cursor-pointer hover:text-foreground transition-colors">
                  Vol <span className="text-foreground font-medium">
                    ${typeof volume24h === 'number' ? volume24h.toFixed(0) : volume24h}M
                  </span>
                </span>
              </PopoverTrigger>
              <PopoverContent className="w-64 text-sm bg-card border border-border shadow-lg z-50">
                <p>{t('tokens.volume24hTooltip')}</p>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>

      <div className="h-16 sm:h-20 -mx-2 mb-3 sm:mb-4">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="priceGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={isPositive ? "hsl(var(--success))" : "hsl(var(--destructive))"} stopOpacity={0.3} />
                <stop offset="100%" stopColor={isPositive ? "hsl(var(--success))" : "hsl(var(--destructive))"} stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis dataKey="date" hide />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
                fontSize: '12px',
              }}
              formatter={(value: number) => [`$${value.toFixed(4)}`, 'Price']}
              labelFormatter={(label) => label || ''}
            />
            <Area
              type="monotone"
              dataKey="value"
              stroke={isPositive ? "hsl(var(--success))" : "hsl(var(--destructive))"}
              strokeWidth={2}
              fill="url(#priceGradient)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:gap-4 pt-3 sm:pt-4 border-t border-border/50">
        <Popover>
          <PopoverTrigger asChild>
            <div className="p-2 sm:p-3 bg-secondary/50 rounded-lg sm:rounded-xl cursor-pointer hover:bg-secondary/70 transition-colors">
              <p className="text-[10px] sm:text-xs text-muted-foreground mb-0.5 sm:mb-1">{t('tokens.totalSupply')}</p>
              <p className="text-sm sm:text-lg font-bold text-foreground">
                {(totalSupply / 1e9).toFixed(2)}B
              </p>
            </div>
          </PopoverTrigger>
          <PopoverContent className="w-64 text-sm bg-card border border-border shadow-lg z-50">
            <p>{t('tokens.totalSupplyTooltip')}</p>
          </PopoverContent>
        </Popover>
        <Popover>
          <PopoverTrigger asChild>
            <div className="p-2 sm:p-3 bg-secondary/50 rounded-lg sm:rounded-xl cursor-pointer hover:bg-secondary/70 transition-colors">
              <p className="text-[10px] sm:text-xs text-muted-foreground mb-0.5 sm:mb-1">{t('tokens.totalStaked')}</p>
              <p className="text-sm sm:text-lg font-bold text-foreground">
                {(totalStaked / 1e9).toFixed(2)}B
                <span className="text-[10px] sm:text-xs text-success ml-1">+{stakedChange}%</span>
              </p>
            </div>
          </PopoverTrigger>
          <PopoverContent className="w-64 text-sm bg-card border border-border shadow-lg z-50">
            <p>{t('tokens.totalStakedTooltip')}</p>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};

export default TokenPriceCard;